import React from 'react';
import { API } from 'aws-amplify';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import { useHistory } from 'react-router-dom';

import reducer from './reducer';

const initialState = {
    page: 0,
    rowsPerPage: 25,
    filterString: '',
    sort: 'LastName Asc',
    recOffset: 0,
    searchValue: '',
    searchKeys: ['LastName', 'FirstName'],
    searchString: '',
    filterObjectString: '',
    sortKey: 'LastName',
    sortDir: 'Asc',
    isFetching: true,
    filters: {},
    totalRecordCount: 0,
    currentCount: 0,
    headers: {},
    list: [],
    hasMore: true,
    isInitialized: false,
};

const useRequestBuilder = (savedState) => {
    const [requestObj, dispatch] = React.useReducer(reducer, savedState ? savedState : initialState);
    const history = useHistory();

    const retrieveStudents = async (_filterString, _sort, _recOffset) => {
        dispatch({ type: 'fetching' });
        let requestBody = {
            filterString: _filterString !== undefined ? _filterString : requestObj.filterString,
            sort: !!_sort ? _sort : requestObj.sort,
            recOffset: _recOffset !== undefined ? _recOffset : requestObj.recOffset,
        };

        var id = toast.loading('Retrieving student list...');
        try {
            let {
                list: _list,
                headers: _headers,
                filters: _filters,
            } = await API.post('ShipyardAPI', '/students', {
                body: requestBody,
            });

            _list = _list.map((item) => {
                let _parsedStatus = _filters.StatusID.filters.find((status) => status.id === item.StatusID);
                return {
                    ForReview: item.StatusID === 5,
                    href: `/students/application/${item.AppInstId}`,
                    ...item,
                    StatusID: {
                        id: item.StatusID,
                        label: _parsedStatus.label,
                        hex: _parsedStatus.hex,
                    },
                };
            });

            let newList = requestBody.recOffset === 0 ? _list : [...requestObj.list, ..._list];

            const newState = {
                list: newList,
                currentCount: newList.length,
                totalRecordCount: Number.isInteger(_list?.[0]?.TotalRecordCount) ? _list[0].TotalRecordCount : 0,
                hasMore: newList.length < _list?.[0]?.TotalRecordCount,
                isInitialized: true,
                filters: _filters,
                headers: [..._headers],
                recOffset: requestBody.recOffset + 9999,
            };

            if (requestBody.filterString === '') newState.totalUnfilteredCount = _list?.[0]?.TotalRecordCount;

            dispatch({ type: 'update', newState });
        } catch (e) {
            toast.error(t('General.AjaxError'));
            console.error(e);
        } finally {
            if (id) toast.dismiss(id);
            dispatch({ type: 'fetched' });
        }
    };

    const handleSort = (key) => {
        let newState = {};

        if (key !== requestObj.sortKey) {
            newState.sortKey = key;
            newState.sortDir = 'Asc';

            newState.sort = `${key} Asc`;
        } else {
            newState.sortDir = requestObj?.sortDir === 'Asc' ? 'Desc' : 'Asc';

            newState.sort = `${key} ${newState.sortDir}`;
        }

        dispatch({ type: 'sort', newState });

        retrieveStudents(requestObj.filterString, newState.sort, 0);
    };

    const handleFilterString = async (newVal, type) => {
        let newFilterString = '',
            newSearchString = '',
            newTotalString = '',
            newState = {
                page: 0,
            };

        switch (type) {
            case 'search':
                if (newVal !== '') {
                    newSearchString = requestObj?.searchKeys.map((key) => `${key} LIKE '%${newVal}%'`).join(' OR ');

                    newTotalString =
                        requestObj?.filterObjectString !== ''
                            ? `${requestObj.filterObjectString} AND (${newSearchString})`
                            : `(${newSearchString})`;
                } else {
                    newTotalString = requestObj?.filterObjectString;
                }

                newState.filterString = newTotalString;
                newState.searchString = newSearchString;
                newState.searchValue = newVal;
                break;
            case 'filter':
                let selectedFilterEntries = Object.entries(newVal);
                newFilterString = selectedFilterEntries
                    .map(([key, filters]) => {
                        return filters?.length > 0 ? `${key} IN (${filters.join(', ')})` : '';
                    })
                    .filter((str) => str !== '')
                    .join(' AND ');

                if (newFilterString !== '') {
                    newTotalString =
                        requestObj?.searchString !== '' ? `${newFilterString} AND (${requestObj?.searchString})` : newFilterString;
                } else {
                    newTotalString = requestObj?.searchString !== '' ? `(${requestObj?.searchString})` : '';
                }

                newState.filterString = newTotalString === '' ? null : newTotalString;
                newState.filterObjectString = newFilterString;
                newState.selectedFilters = newVal;
                break;
            default:
                return;
        }

        if (newState.filterString !== requestObj.filterString) {
            dispatch({ type: 'filter', newState });
            retrieveStudents(newState.filterString, requestObj.sort, 0);
        }
    };

    const loadMore = async () => {
        await retrieveStudents();
    };

    const handleNav = async (href) => {
        dispatch({ type: 'fetching' });
        history.push(href, {
            ...requestObj,
            recOffset: 0,
        });
    };

    const handlePageChange = (event, newPage) => {
        dispatch({
            type: 'update',
            newState: {
                page: newPage,
            },
        });
    };

    const handleRowsPerPageChange = (event) => {
        dispatch({
            type: 'update',
            newState: {
                page: 0,
                rowsPerPage: event.target.value,
            },
        });
    };

    return {
        ...requestObj,
        retrieveStudents,
        handleFilterString,
        handleSort,
        loadMore,
        handleNav,
        handlePageChange,
        handleRowsPerPageChange,
    };
};

export default useRequestBuilder;
